import { NetworkStatus, gql, useQuery } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import { Heading, Link } from '@apollo/orbit';
import { groupBy, partition } from 'lodash';
import React, { Fragment, useEffect, useMemo } from 'react';

import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { test } from 'src/components/common/typeahead/fuzzy';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { format } from 'src/lib/format';
import * as ApolloTypes from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';
import { stringCompare } from 'src/lib/sort';

import { GraphErrorDisplay } from './GraphErrorDisplay';
import { NoVariantsSupergraphsListItem } from './NoVariantsSupergraphsListItem';
import {
  SupergraphsListItem,
  SupergraphsListItemGraphFragment,
} from './SupergraphsListItem';

export const SupergraphsListServiceFragment = gql<
  ApolloTypes.SupergraphsListServiceFragment,
  never
>`
  fragment SupergraphsListServiceFragment on Service {
    id
    graphType
    title
    avatarUrl
    variants {
      id
      isFavoriteOfCurrentUser
    }
    ...SupergraphsListItemGraphFragment
  }
  ${SupergraphsListItemGraphFragment}
`;

export const supergraphsListQuery = gql<
  ApolloTypes.SupergraphsListQuery,
  ApolloTypes.SupergraphsListQueryVariables
>`
  query SupergraphsListQuery($accountId: ID!) {
    account(id: $accountId) {
      id
      graphsConnection {
        edges {
          node {
            id
            ...SupergraphsListServiceFragment
          }
        }
      }
    }
  }
  ${SupergraphsListServiceFragment}
`;

export const SupergraphsList = ({
  accountId,
  shouldSeeSupergraphAccountView,
  supergraphListSearch,
}: {
  accountId: string;
  shouldSeeSupergraphAccountView: boolean;
  supergraphListSearch: string | undefined;
}) => {
  const { data, networkStatus, error } = useQuery(supergraphsListQuery, {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    pollInterval: 30_000,
    variables: {
      accountId,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  // Group All Graphs by Type (For Metrics)
  const allGraphsGroupedByType = useMemo(() => {
    const allGraphs = data?.account?.graphsConnection?.edges || [];
    return groupBy(allGraphs, ({ node }) => (node ? node.graphType : null));
  }, [data]);

  // Filter All Graphs by Type (For Rendering, specifically when search is used)
  const filteredGraphs = useMemo(
    () =>
      data?.account?.graphsConnection?.edges?.filter(({ node }) =>
        test(supergraphListSearch || '', node?.title || ''),
      ),
    [data, supergraphListSearch],
  );

  // Group All Filtered Graphs by Type (for Rendering)
  const graphsGroupedByType = useMemo(
    () => groupBy(filteredGraphs, ({ node }) => (node ? node.graphType : null)),
    [filteredGraphs],
  );

  // Group Favorited graphs
  const graphsWithFavs = useMemo(
    () =>
      data?.account?.graphsConnection?.edges?.filter(
        ({ node }) =>
          node &&
          node.variants.some(
            ({ isFavoriteOfCurrentUser }) => !!isFavoriteOfCurrentUser,
          ),
      ) || [],
    [data],
  );

  // Partition Graphs by Deployed/Configured State (For Metrics)
  // - Deployed graphs have one or more variants
  // - Unconfigured graphs have zero variants (can also be considered `undeployed`)
  const [deployedGraphs, unconfiguredGraphs] = useMemo(() => {
    return partition(
      Object.values(allGraphsGroupedByType).flat(),
      ({ node }) => node && node.variants.length > 0,
    );
  }, [allGraphsGroupedByType]);

  // Side Effect for Metrics Tracking
  // Note: This tracks metrics only when the graph data (which is cached) changes,
  // avoiding unnecessary triggers from polling or user searches
  useEffect(() => {
    if (allGraphsGroupedByType) {
      // Calculate total graphs
      const totalGraphs = Object.values(allGraphsGroupedByType).flat().length;

      // Proceed only if there are graphs
      if (totalGraphs > 0) {
        ampli.homePageViewed({
          'Total Graphs': totalGraphs,
          'Deployed Graphs': deployedGraphs.length,
          'Unconfigured Graphs': unconfiguredGraphs.length,
        });
      }
    }
    // Note: We include deployedGraphs and unconfigured Graphs in dependencies
    // because they are derived from allGraphsGroupedByType and are necessary for metrics
  }, [
    allGraphsGroupedByType,
    deployedGraphs.length,
    unconfiguredGraphs.length,
  ]);

  if (networkStatus === NetworkStatus.loading && !data)
    return <Loading size="md" />;

  if (error) return <GraphErrorDisplay error={error} />;

  if (!data || !data.account)
    return (
      <GraphErrorDisplay
        error={{
          message: (
            <>
              Organization <span className="font-mono">{accountId}</span> was
              not found
            </>
          ),
        }}
      />
    );

  return (
    <>
      {supergraphListSearch && filteredGraphs?.length === 0 ? (
        <>
          <Heading className="mb-8 mt-10 h-4" size="2xl">
            0 matching graphs
          </Heading>
          <div>
            There are no matching graphs, please change your search parameter.
          </div>
        </>
      ) : null}
      {!supergraphListSearch && !!graphsWithFavs.length && (
        <>
          <Heading className="mb-8 mt-10 h-4" size="2xl">
            {graphsWithFavs.length}{' '}
            {format.pluralize(graphsWithFavs.length, 'Favorite')}
          </Heading>
          {graphsWithFavs.map(
            ({ node: graph }) =>
              graph && (
                <SupergraphsListItem
                  searchText={supergraphListSearch}
                  key={graph.id}
                  graph={graph}
                  isInFavSection={true}
                />
              ),
          )}
        </>
      )}
      {Object.entries(graphsGroupedByType).map(([graphType, graphs]) => {
        const graphLabel =
          graphType === 'CLOUD_SUPERGRAPH'
            ? 'Cloud supergraph'
            : shouldSeeSupergraphAccountView
              ? 'Classic graph'
              : 'Deployed graph';
        const [graphsWithVariants, graphsNoVariants] = partition(
          graphs,
          ({ node }) => node && node.variants.length > 0,
        ).map((graphList) =>
          graphList.sort(({ node: g1 }, { node: g2 }) =>
            stringCompare(g1?.title || '', g2?.title || '', true),
          ),
        );

        return (
          <Fragment key={graphType}>
            <Heading className="mb-8 mt-10 h-4" size="2xl">
              {graphsWithVariants?.length}{' '}
              {supergraphListSearch ? 'Matching ' : ''}
              {format.pluralize(graphsWithVariants?.length, graphLabel)}
            </Heading>
            {graphsWithVariants?.map(
              ({ node: graph }) =>
                graph && (
                  <SupergraphsListItem
                    searchText={supergraphListSearch}
                    key={graph.id}
                    graph={graph}
                  />
                ),
            )}
            {graphType === 'CLOUD_SUPERGRAPH' && !!graphsNoVariants?.length && (
              <div className="flex items-center">
                <Heading className="mb-8 mt-10 h-4 items-center" size="2xl">
                  {graphsNoVariants?.length}{' '}
                  {supergraphListSearch ? 'Matching ' : ''}
                  {format.pluralize(
                    graphsNoVariants?.length,
                    'Cloud supergraphs without variant',
                  )}
                  <Tooltip
                    interactive
                    label={
                      <div>
                        {graphLabel} need at least one variant to use Studio
                        features.{' '}
                        <Link
                          className="text-inherit"
                          isExternal
                          to={DocsRouter.path('AddAVariant')}
                        >
                          Learn how to add a variant
                        </Link>
                      </div>
                    }
                  >
                    <IconInfo className="ml-2 mt-1" tabIndex={0} />
                  </Tooltip>
                </Heading>
              </div>
            )}
            {graphType === 'CLASSIC' && !!graphsNoVariants?.length && (
              <Heading className="mb-8 mt-10 flex h-4" size="2xl">
                {graphsNoVariants?.length}{' '}
                {supergraphListSearch ? 'Matching ' : ''}
                {format.pluralize(
                  graphsNoVariants?.length,
                  shouldSeeSupergraphAccountView
                    ? 'Unconfigured classic graph'
                    : 'Unconfigured graph',
                )}
              </Heading>
            )}
            {graphsNoVariants?.map(
              ({ node: graph }) =>
                graph && (
                  <NoVariantsSupergraphsListItem
                    key={graph.id}
                    graphId={graph.id}
                    graphType={graph.graphType}
                    graphTitle={graph.title}
                    avatarUrl={graph.avatarUrl}
                  />
                ),
            )}
          </Fragment>
        );
      })}
    </>
  );
};
